<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Eye, Gamepad2, Play, RefreshCw, RotateCcw, ScrollText, UserRound } from '@lucide/vue'
import { Badge, Button, EmptyState, PageHeader, Section, Select, Spinner, Table, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { createCreatorProjectPreview, listCreatorProjects, retryCreatorProject, startCreatorProject } from '../api/creator-projects.js'
import { visualGamePreviewUrl } from '../lib/visual-game-preview-url.js'

const { locale, t } = useI18n()
const router = useRouter()
const projects = ref([])
const status = ref('')
const loading = ref(false)
const error = ref('')
const previewingProjectId = ref(null)
const startingProjectId = ref(null)
const previewLocale = ref('en')

const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.ugc.all') },
  ...['queued', 'generating', 'pending_review', 'generation_failed', 'review_rejected', 'published', 'archived']
    .map(value => ({ value, label: t(`visualGameAdmin.ugc.status.${value}`) })),
])
const previewLocaleOptions = computed(() => ['zh', 'en', 'ja', 'ko', 'es', 'pt'].map(value => ({ value, label: t(`visualGameAdmin.ugc.previewLocales.${value}`) })))

const columns = computed(() => [
  { key: 'game', label: t('visualGameAdmin.ugc.table.game'), width: '205px' },
  { key: 'creator', label: t('visualGameAdmin.ugc.table.creator'), width: '150px' },
  { key: 'language', label: t('visualGameAdmin.ugc.table.language'), width: '55px' },
  { key: 'created_at', label: t('visualGameAdmin.ugc.table.createdAt'), width: '125px' },
  { key: 'updated_at', label: t('visualGameAdmin.ugc.table.updatedAt'), width: '125px' },
  { key: 'status', label: t('visualGameAdmin.ugc.table.status'), width: '95px' },
  { key: 'actions', label: t('visualGameAdmin.ugc.table.actions'), width: '270px', align: 'right', fixed: 'right' },
])

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    projects.value = (await listCreatorProjects(status.value || null)).projects
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function openProject(project) {
  router.push(`/creator-projects/${project.id}`)
}

function openLogs(project) {
  router.push(`/creator-projects/${project.id}/logs`)
}

async function preview(project) {
  const previewWindow = window.open('about:blank', '_blank')
  if (previewWindow === null) {
    error.value = t('visualGameAdmin.ugc.previewPopupBlocked')
    return
  }
  previewWindow.opener = null
  previewingProjectId.value = project.id
  error.value = ''
  try {
    const result = await createCreatorProjectPreview(project.id)
    const url = visualGamePreviewUrl(window.location.origin)
    url.searchParams.set('scenarioId', result.pack_id)
    url.searchParams.set('versionId', result.version_id)
    url.searchParams.set('preview', '1')
    url.searchParams.set('previewToken', result.preview_token)
    url.searchParams.set('locale', previewLocale.value)
    previewWindow.location.replace(url)
  } catch (requestError) {
    previewWindow.close()
    error.value = requestError.message
  } finally {
    previewingProjectId.value = null
  }
}

async function startProduction(project) {
  startingProjectId.value = project.id
  error.value = ''
  try {
    if (project.status === 'queued') await startCreatorProject(project.id)
    else if (project.status === 'generation_failed') await retryCreatorProject(project.id)
    else throw new Error(`Unhandled creator project production status: ${project.status}`)
    await load()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    startingProjectId.value = null
  }
}

function creatorName(project) {
  if (project.creator.account_type === 'guest') return t('visualGameAdmin.ugc.guestCreator')
  if (project.creator.display_name) return project.creator.display_name
  return t('visualGameAdmin.ugc.unnamedCreator')
}

function creatorIdentity(project) {
  if (project.creator.email) return project.creator.email
  return project.creator_user_id
}

function formatProjectDate(value) {
  if (value === null) return '—'
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(value))
}

function statusVariant(value) {
  switch (value) {
    case 'queued': return 'warning'
    case 'pending_review': return 'warning'
    case 'published': return 'success'
    case 'generation_failed':
    case 'review_rejected': return 'danger'
    case 'generating': return 'info'
    case 'archived': return 'neutral'
    default: throw new Error(`Unhandled creator project status: ${value}`)
  }
}
</script>

<template>
  <div class="ugc-list vga-page" data-insp-path="apps/visual-game-admin/src/pages/CreatorProjects.vue">
    <PageHeader :title="t('visualGameAdmin.ugc.title')" />
    <p class="ugc-list__subtitle">{{ t('visualGameAdmin.ugc.subtitle') }}</p>

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.ugc.statusFilter') }}</label>
          <Select v-model="status" :options="statusOptions" size="sm" @update:model-value="load" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.ugc.previewLanguage') }}</label>
          <Select v-model="previewLocale" :options="previewLocaleOptions" size="sm" />
        </div>
        <template #actions>
          <Button variant="secondary" size="sm" :loading="loading" @click="load">
            <RefreshCw :size="14" /> {{ t('visualGameAdmin.ugc.refresh') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <Section :padded="false">
      <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.ugc.loading') }}</span></div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <EmptyState v-else-if="projects.length === 0" :title="t('visualGameAdmin.ugc.empty')" />
      <Table v-else :columns="columns" :rows="projects" clickable @row-click="openProject">
        <template #game="{ row }">
          <div class="ugc-list__game">
            <strong>{{ row.title }}</strong>
            <span>{{ row.idea_text }}</span>
          </div>
        </template>
        <template #creator="{ row }">
          <div class="ugc-list__creator">
            <UserRound :size="16" />
            <span><strong>{{ creatorName(row) }}</strong><small>{{ creatorIdentity(row) }}</small></span>
          </div>
        </template>
        <template #language="{ row }">{{ row.language.toUpperCase() }}</template>
        <template #created_at="{ row }">{{ formatProjectDate(row.created_at) }}</template>
        <template #updated_at="{ row }">{{ formatProjectDate(row.updated_at) }}</template>
        <template #status="{ row }">
          <Badge :variant="statusVariant(row.status)" size="sm">{{ t(`visualGameAdmin.ugc.status.${row.status}`) }}</Badge>
        </template>
        <template #actions="{ row }">
          <div class="ugc-list__actions">
            <Button v-if="row.status === 'queued'" size="sm" :loading="startingProjectId === row.id" @click.stop="startProduction(row)">
              <Play :size="14" /> {{ t('visualGameAdmin.ugc.startProduction') }}
            </Button>
            <Button v-if="row.status === 'generation_failed'" size="sm" :loading="startingProjectId === row.id" @click.stop="startProduction(row)">
              <RotateCcw :size="14" /> {{ t('visualGameAdmin.ugc.retryProduction') }}
            </Button>
            <Button
              v-if="row.status === 'pending_review'"
              variant="secondary"
              size="sm"
              :loading="previewingProjectId === row.id"
              data-testid="creator-project-preview"
              @click.stop="preview(row)"
            >
              <Gamepad2 :size="14" /> {{ t('visualGameAdmin.ugc.previewAction') }}
            </Button>
            <Button variant="secondary" size="sm" @click.stop="openProject(row)">
              <Eye :size="14" /> {{ t(row.status === 'pending_review' ? 'visualGameAdmin.ugc.reviewActionShort' : 'visualGameAdmin.ugc.viewActionShort') }}
            </Button>
            <Button variant="ghost" size="sm" @click.stop="openLogs(row)">
              <ScrollText :size="14" /> {{ t('visualGameAdmin.ugc.logsAction') }}
            </Button>
          </div>
        </template>
      </Table>
    </Section>
  </div>
</template>

<style scoped>
.ugc-list__subtitle { margin: calc(var(--drama-space-lg) * -1) 0 0; color: var(--drama-color-text-muted); }
.ugc-list__game { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.ugc-list__game strong { overflow: hidden; text-overflow: ellipsis; }
.ugc-list__game span { overflow: hidden; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); text-overflow: ellipsis; white-space: nowrap; }
.ugc-list__creator { display: flex; align-items: center; gap: var(--drama-space-sm); min-width: 0; }
.ugc-list__creator > span { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.ugc-list__creator strong,
.ugc-list__creator small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ugc-list__creator small { color: var(--drama-color-text-muted); }
.ugc-list__actions { display: flex; justify-content: flex-end; gap: var(--drama-space-xs); }
</style>
